/* cf-rake-height-assist.js - Click Fencing, 2026-09-01 (later session)
 *
 * THE DEFECT, reported by Pete and reproduced on the live glass calculator:
 *   "the add to cart button doesnt work!"
 *
 * On a raked glass section with no Retained Height chosen, Add to Cart did NOTHING.
 * No navigation, no alert, no console error - measured with a persisted log across the
 * redirect: zero /cart/* requests, addToCart never entered, no jsError, no beforeunload.
 *
 * WHY. assets/cf-glass-preview-fixes.js (fix 5) correctly refuses to PRICE a rake panel
 * until the customer picks a retained height - we cannot price a panel whose height we do
 * not know. But it enforced that with:
 *
 *     if (btn) { btn.disabled = true; btn.setAttribute('data-cf-rake-blocked', '1'); }
 *
 * A disabled button does not dispatch a click at all, so the page is silent. The one
 * sentence explaining why sits in the quote breakdown - MEASURED at y=9920 while the
 * button is at y=13317 on a 15,476px page. The customer is told the reason 3,397 pixels
 * above the thing they just pressed, which is to say: not told.
 *
 * Pete's standing rule, 2026-09-01: never dead-end a customer, and never show them an
 * error saying we cannot do it. Asking for a measurement is legitimate - doing it with a
 * dead button three screens from the explanation is not.
 *
 * WHAT THIS DOES. It does not change the pricing decision, which is right: an unpriced
 * rake panel still stays out of the quote until a height is chosen. It changes the
 * INTERACTION:
 *
 *   1. Keeps Add to Cart ENABLED. A MutationObserver on the button's disabled attribute
 *      re-enables it the instant fix 5 disables it, so the button always responds. Only a
 *      disable carrying fix 5's own data-cf-rake-blocked marker is released - a disable
 *      set by anything else is left alone.
 *   2. On click, if a raked section still has no height, it takes the customer STRAIGHT
 *      TO THE FIELD: scrolls the Retained Height select into view, focuses it, opens it
 *      where the browser allows, and outlines it briefly.
 *   3. Puts the prompt NEXT TO THE FIELD, phrased as a question rather than an error, and
 *      in the site's amber-advice styling rather than the red error styling.
 *
 * So the button works, the customer is taken to the one input that is missing, and there
 * is no red panel telling them their fence cannot be built.
 *
 * Loads after cf-glass-preview-fixes.js. Guarded to the glass calculator pages only.
 */
(function () {
  'use strict';

  if (window.__cfRakeAssist) return;
  window.__cfRakeAssist = true;

  var PATHS = ['/pages/glass-fencing', '/pages/fence-calculator'];
  var path = (location.pathname || '/').replace(/\/+$/, '') || '/';
  if (PATHS.indexOf(path) === -1) return;

  var BLOCK_ATTR = 'data-cf-rake-blocked';
  var FIELD_NOTE_ID = 'cf-rake-field-note';

  function inst() { return window.glassCalculator || window.glassFencingCalculator || null; }

  /* Which raked sections still have no retained height? Reads the real select first, and
     falls back to state, matching how cf-glass-preview-fixes decides the same thing. */
  function missingSections() {
    var out = [];
    try {
      var c = inst();
      var secs = c && c.state && c.state.sections;
      if (!secs) return out;
      Object.keys(secs).map(Number).sort(function (a, b) { return a - b; }).forEach(function (n) {
        var s = secs[n];
        if (!s || !s.hasRaked) return;
        var sel = document.getElementById('section-' + n + '-retained-height');
        var chosen = sel ? String(sel.value || '').trim() : String(s.retainedHeight || '').trim();
        if (!chosen) out.push(n);
      });
    } catch (e) {}
    return out;
  }

  function firstSelect(missing) {
    for (var i = 0; i < missing.length; i++) {
      var el = document.getElementById('section-' + missing[i] + '-retained-height');
      if (el) return el;
    }
    return null;
  }

  /* The prompt belongs beside the control it is about, not in the breakdown. */
  function noteAtField(sel) {
    try {
      var old = document.getElementById(FIELD_NOTE_ID);
      if (old && old.parentNode) old.parentNode.removeChild(old);
      if (!sel || !sel.parentNode) return;
      var d = document.createElement('div');
      d.id = FIELD_NOTE_ID;
      d.setAttribute('style',
        'margin:8px 0 0;padding:10px 12px;border-radius:8px;' +
        'background:#fff8e6;border:1px solid #f0d089;border-left:4px solid #e0a63c;' +
        'color:#6b4c14;font-size:14px;line-height:1.45;');
      d.textContent = 'How high is the wall or step the fence sits on? Pick a retained height '
        + 'and we can price the raked panel for you.';
      sel.parentNode.insertBefore(d, sel.nextSibling);
    } catch (e) {}
  }

  function flash(sel) {
    try {
      var prev = sel.style.boxShadow;
      sel.style.transition = 'box-shadow .2s ease';
      sel.style.boxShadow = '0 0 0 3px rgba(224,166,60,.55)';
      setTimeout(function () { sel.style.boxShadow = prev || ''; }, 1800);
    } catch (e) {}
  }

  function sendToField() {
    var missing = missingSections();
    if (!missing.length) return false;
    var sel = firstSelect(missing);
    if (!sel) return false;
    try {
      noteAtField(sel);
      sel.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(function () {
        try {
          sel.focus({ preventScroll: true });
          /* Chrome/Edge only; harmless where unsupported. */
          if (typeof sel.showPicker === 'function') { try { sel.showPicker(); } catch (e) {} }
          flash(sel);
        } catch (e) {}
      }, 320);
    } catch (e) {}
    return true;
  }

  /* 1. Never leave the button dead. Release ONLY the block fix 5 marks as its own. */
  function releaseButton() {
    try {
      var btn = document.getElementById('add-to-cart');
      if (!btn) return;
      if (btn.disabled && btn.getAttribute(BLOCK_ATTR)) btn.disabled = false;
    } catch (e) {}
  }

  function watchButton() {
    try {
      var btn = document.getElementById('add-to-cart');
      if (!btn || btn.__cfRakeWatched) return;
      btn.__cfRakeWatched = true;
      releaseButton();
      if (window.MutationObserver) {
        new MutationObserver(releaseButton).observe(btn, { attributes: true, attributeFilter: ['disabled'] });
      }
    } catch (e) {}
  }

  /* 2. Capture phase so this runs before the calculator's own handler and can redirect the
        customer to the field instead of letting a half-configured quote through. */
  document.addEventListener('click', function (ev) {
    try {
      var t = ev.target;
      var b = (t && t.closest) ? t.closest('#add-to-cart') : null;
      if (!b) return;
      if (!missingSections().length) return;
      ev.preventDefault();
      ev.stopImmediatePropagation();
      sendToField();
    } catch (e) {
      /* Never let this stop a sale: on any error the click proceeds untouched. */
    }
  }, true);

  /* Clear the field note the moment a height is chosen. */
  document.addEventListener('change', function (e) {
    try {
      if (!e.target || !/^section-\d+-retained-height$/.test(e.target.id || '')) return;
      if (!missingSections().length) {
        var n = document.getElementById(FIELD_NOTE_ID);
        if (n && n.parentNode) n.parentNode.removeChild(n);
      }
      releaseButton();
    } catch (e2) {}
  }, true);

  var tries = 0;
  (function tick() {
    if (tries++ > 300) return;
    watchButton();
    releaseButton();
    setTimeout(tick, 500);
  })();

  try { console.log('[cf-rake-height-assist] installed'); } catch (e) {}
})();
